export type EventBasic = {
  id: number;
  name: string;
  starts_at: string;
  ends_at: string;
  starts_at_timestamp: number;
  ends_at_timestamp: number;
  event_type: number;
  // Optional region used for the location chip on the events list (e.g. 'europe',
  // 'north_america', 'india'). Not yet populated by the API — chip stays hidden until it is.
  region?: string | null;
  // IANA timezone of the event venue (e.g. 'Europe/Berlin', 'America/Los_Angeles').
  // Absent until the API ships it — formatting falls back to UTC pass-through.
  timezone?: string | null;
}

export type EventData = EventBasic & {
  id: number,
  name: string,
  starts_at: string
  ends_at: string
  event_type: number,
  video_url: string;
  cover_image: string;
  vimeo_chat_url: string;
  skills: Array<any>;
  sessions: EventSession[],
  speakers: EventSpeaker[],
  stages: EventStage[],
  tracks: EventTrack[],
  session_types: EventSessionType[],
  menu_items: EventMenuItem[],
  workshop_registration_opens_at: string,
  // The app shows the Program Preview instead of the agenda while this is true (TAL-11054).
  program_preview?: boolean,
  day_overview?: EventDayOverview | null,
}

// "Two days at a glance": the cards of the Program Preview. `date` is a calendar day (YYYY-MM-DD) and
// `time` a display label ("08:00", "All day"), so neither needs a timezone.
export type EventDayOverview = {
  heading?: string,
  intro?: string,
  note?: string,
  days: Array<{
    date: string,
    blocks: Array<{time: string, title: string, description?: string}>,
  }>,
}

export type EventUserInfo = {
  id: number,
  name: string,
  event_type: number,
  has_connections: boolean,
  require_onboarding: boolean,
  ticket_type_id: number,
  is_partner_ticket: boolean,
}

export type EventPartners = {
  partners: CompanyEventPartnership[],
  goodies: CompanyEventPartnershipGoodies[],
  activities: CompanyEventPartnershipBoothActivities[],
}

export type EventStage = {
  name: string,
  as_string: string,
  capacity: number,
  id: number
}

export type EventMenuItem = {
  label: string,
  slug: string,
  url: string,
  type: number,
  id: number
}

export type EventSession = {
  id: number,
  title: string,
  description: string,
  status: number,
  featured: boolean,
  starts_at: string | null,
  ends_at: string | null,
  starts_at_timestamp: number,
  ends_at_timestamp: number,
  // Which day of the event (0 = the first), counted in the event's timezone; null without a start time.
  event_day: number | null,
  location: string,
  speakers?: EventSpeaker[],
  track?: EventTrack,
  stage?: EventStage,
  stage_search_name: string,
  live_coding?: boolean,
  event_id: number,
  live_url: string,
  recording_url: string,
  is_workshop: boolean,
  disable_qa: boolean,
  workshop_capacity: number,
  workshop_requirements: string,
  search_tags: string[],
  // The keywords shown on the session page (Sessionize tags).
  tags?: string[],
  location_override?: string,
  session_type?: number,
  waitlist_position?: number,
}

export type EventTrack = {
  id: number,
  name: string
}

export type EventSessionType = {
  id: number,
  name: string
}

export type EventSpeaker = {
  id: number,
  event_id: number,
  full_name: string,
  first_name?: string,
  last_name?: string,
  company_position?: string,
  company_name?: string,
  // Free-text "Position at Company" line. Curated, not written by the Sessionize
  // import — most speakers have none.
  tagline?: string,
  chips?: string,
  link_twitter?: string,
  link_linkedin?: string,
  link_github?: string,
  link_website?: string,
  image_url?: string,
  bio?: string,
  user_id?: number
}

export type CompanyEventPartnership = {
  id: number,
  company_name: string,
  event_id: number,
  company_id: number,
  company_congress_logo_url: string,
  company_congress_cover_url: string,
  company_booth_number: string
  company_introduction: string,
  link_one?: string,
  link_one_title?: string,
  link_two?: string,
  link_two_title?: string,
  link_three?: string,
  link_three_title?: string,
  link_four?: string,
  link_four_title?: string,
  link_five?: string,
  link_five_title?: string,
  facebook_url?: string,
  linkedin_url?: string,
  twitter_url?: string,
  instagram_url?: string,
  tiktok_url?: string,
  github_url?: string,
  website_url?: string,
  company_sessions: EventSession[],
  company_goodies: CompanyEventPartnershipGoodies[],
  company_booth_activities: CompanyEventPartnershipBoothActivities[],
  latest_jobs: JobBasic[],
  company_people: EventSpeaker[],
}

export type CompanyEventPartnershipGoodies = {
  id: number,
  event_id: number,
  company_partnership_id: number,
  title: string,
  description: string,
  image: string,
  company_name: string,
}

export type CompanyEventPartnershipBoothActivities = {
  id: number,
  company_partnership_id: number,
  title: string,
  description: string,
  image: string,
  company_name: string,
}


export type EventTicketValidation = {
  event_id: number,
  existing_user: boolean,
  ticket_email: string,
  ticket_name: string,
  access_token?: string,
  ticket_access_expired: boolean,
  ticket_token: string,
  ticked_assigned_to_current_user: boolean
}

export type EventTicket = {
  id: number,
  event: EventBasic,
}

export const EventViewEnum = {
  Timeline: { label: 'Timeline', value: 'timeline' },
  Stage: { label: 'Stage', value: 'stage' },
  Track: { label: 'Track', value: 'track' },
} as const;
export type EventViewOption = typeof EventViewEnum[keyof typeof EventViewEnum];

export type SessionQuestion = {
  id: number;
  user_id: number;
  question: string;
  submitted_at: string;
  checked_question: boolean;
};

export type BookmarkedSession = {
  session_id: number;
  waitlist_position: number;
};

// What wad-api answers to the bulk bookmark of an agent's plan.
export type BulkBookmarkSkipReason = 'workshop' | 'unavailable' | 'not_found';
export type BulkBookmarkResult = {
  added: number[];
  already_bookmarked: number[];
  skipped: {session_id: number, reason: BulkBookmarkSkipReason}[];
};

// Seat availability for a single workshop session. `seats` is the number of
// confirmed seats taken (availableSeats = workshop_capacity - seats).
export type WorkshopSeatCount = {
  seats: number;
  waitlist_full: boolean;
};

export type EventUserContactDetails = {
  email: string;
  first_name: string;
  last_name: string;
  job_position: string;
  company: string;
  country: string;
  tito_qr_code_url: string;
  about: string;
  linkedin: string;
  github: string;
  twitter: string;
  bluesky: string;
  mastodon: string;
  facebook: string;
  website: string;
  phone_number: string;
  notes: string;
  image_url?: string;
  connection_status?: number;
  incoming_request?: number;
  connected_at?: string;
  is_visible_to_attendees: boolean;
  has_visited_homepage: boolean;
  hide_from_attendees: boolean;
  interests_reasons: Array<{id: number, name: string}>;
  interests_topics: Array<{id: number, name: string}>;
  interests_options: {reasons: Array<{id: number, name: string}>, topics: Array<{id: number, name: string}>}
}

export type EventAttendeeDetails = {
  connection_status?: number;
  image_url?: string;
  first_name: string;
  last_name: string;
  job_position: string;
  company: string;
  country: string;
  about?: string;
  linkedin?: string;
  github?: string;
  twitter?: string;
  bluesky?: string;
  mastodon?: string;
  facebook?: string;
  website?: string;
  incoming_request?: boolean;
  email?: string;
  phone_number?: string;
  notes?: string;
  connected_at?: string;
}


export const EventSessionStatusEnum = {
  Upcoming: { label: '', value: 'upcoming', statusClass: 'wad4-session-header-status--upcoming' },
  StartingSoon: { label: 'Starting soon', value: 'starting-soon', statusClass: 'wad4-session-header-status--starting-soon' },
  Live: { label: 'Live now', value: 'live', statusClass: 'wad4-session-header-status--live' },
  Over: { label: 'Session is over', value: 'over', statusClass: 'wad4-session-header-status--over' },
  Cancelled: { label: 'Session has been cancelled', value: 'cancelled', statusClass: 'wad4-session-header-status--cancelled' },
} as const;
export type EventSessionStatus = typeof EventSessionStatusEnum[keyof typeof EventSessionStatusEnum];

export const EventBookmarkTypeEnum = {
  Session: { label: 'Session', value: 'session' },
  Speaker: { label: 'Speaker', value: 'speaker' },
  Partner: { label: 'Partner', value: 'partner' },
  Goodies: { label: 'Goodies', value: 'goodies' },
  Activities: { label: 'Activities', value: 'activities' },
} as const;
export type EventBookmarkType = typeof EventBookmarkTypeEnum[keyof typeof EventBookmarkTypeEnum]['value'];

// Per-company/event lead tag managed in the partner portal
export type LeadTag = {
  id: number,
  name: string,
  // Soft-deleted in the partner portal. Still shown on a lead it was assigned to,
  // but rendered disabled (can't toggle on/off).
  is_deleted?: boolean,
}

export type EventLead = {
  id: number,
  email: string;
  first_name: string;
  last_name: string;
  job_position: string;
  company: string;
  country: string;
  tito_qr_code_url: string;
  additional_info: string,
  tags: Array<string>, // merged display names (lead_tags + legacy_tags) — read-only
  lead_tags: Array<LeadTag>,
  legacy_tags: Array<string>, // frozen free-form tags from before id-based tagging
  created_at: string,
  updated_at: string,
  new_scan: boolean|null,
}

export const SyncStatus = {
  PENDING: 'pending',
  SYNCING: 'syncing',
  SYNCED: 'synced', // transient confirmation state; the card lingers briefly then is removed
  FAILED: 'failed', // retryable failure (5xx/network) — stays with Retry/Dismiss
  REJECTED: 'rejected', // permanent rejection (404/422); transient — lingers with a message then removed
} as const;

export type SyncStatusType = typeof SyncStatus[keyof typeof SyncStatus];

export type PendingLead = {
  localId: string;
  eventId: number;
  code: string;
  scannedAt: string;
  syncStatus: SyncStatusType;
  attempts: number;
  lastError: string | null;
}

export type EventAttendee = {
  user_id: number
  name: string
  job_position?: string
  company_name?: string
  image_url?: string
  connection_status: number
  country: string
}

export type EventAttendeeRequestNewConnectionResponse = {
  id: number
  connection_status: number
}

export type EventAttendeeNewConnectionRequest = {
  receiver_user_id: number
  action?: number
}

export type EventAttendeeDirectBadgeScanRequest = {
  ticket_identification: string
}

export type EventAttendeeDirectBadgeScanResponse = {
  user_id: number
}

export type EventAttendeeNotesRequest = {
  attendee_id: number
  notes?: string
}

export interface Paginated<T> {
  data: T[]
  meta: {
    current_page: number
    last_page: number
    total: number
  }
}

export type OrderOpt = 'newest' | 'oldest' | 'az' | 'za';
export type AttendeesFilter = 'all' | 'connected' | 'invitations';

